import React, { useEffect, useState } from "react";
import styles from "./style.module.css";
import Tooltip from "components/Inputs/Tooltip";
import { format, formatDistance } from "date-fns";
import { Button } from "aible";
import { ProgressBar } from "react-bootstrap";
import { useDispatch, useSelector } from "types/store";
import * as foldersReducer from "containers/Folders/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import { toast } from "react-toastify";
import Select from "react-select";
import * as aiServiceReducer from "containers/AiService/reducer";
import TextInput from "../Inputs/TextInput";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { createAgentProfiler, getAgentProfilerStatus, getAgentProfilerResults, getAgentProfiler } from "containers/Agent/reducer";
import { selectOnChangeWrapper } from "utils/select";
import ModelSelector from "../ChatGPT/ModelSelector";
import AgentProfilerCharts from "components/ChatApp/AgentProfilerCharts";
const getProgressBarColor = (val) => {
    if (val < 25) {
        return "danger";
    }
    else if (val < 50) {
        return "warning";
    }
    else if (val < 75) {
        return "info";
    }
    else {
        return "success";
    }
};
const AgentProfiler = () => {
    const dispatch = useDispatch();
    const modelIdWithPrefix = useSelector((state) => chatGPTReducer.getModelId(state));
    const modelId = useSelector((state) => chatGPTReducer.getAiServiceModel(state, modelIdWithPrefix)?.id);
    const selectedFolder = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const agentIQReasoningPrompt = useSelector((state) => chatGPTReducer.getAgentIQReasoningPrompt(state, true, modelIdWithPrefix));
    const aisModels = useSelector((state) => aiServiceReducer.getModels(state));
    const modelOptions = Object.values(aisModels).map((m) => ({ label: m?.name, value: m?.id }));
    const agentIQBaseModel = useSelector((state) => chatGPTReducer.getAgentIQBaseModel(state));
    const agentIQNLQModel = useSelector((state) => chatGPTReducer.getAgentIQNLQModel(state));
    const agentIQReasoningModel = useSelector((state) => chatGPTReducer.getAgentIQReasoningModel(state));
    const foundAgentIqBaseModel = modelOptions.find((m) => m.value === Number(agentIQBaseModel));
    const foundAgentIqNlqModel = modelOptions.find((m) => m.value === Number(agentIQNLQModel));
    const foundAgentIqReasoningModel = modelOptions.find((m) => m.value === Number(agentIQReasoningModel));
    const [evaluatorModelId, setEvaluatorModelId] = useState(undefined);
    const baseConnectionsModel = useSelector((state) => aiServiceReducer.getServiceIdByModelId(state, agentIQBaseModel));
    const reasoningConnectionsModel = useSelector((state) => aiServiceReducer.getServiceIdByModelId(state, agentIQReasoningModel));
    const evaluatorConnectionsModel = useSelector((state) => aiServiceReducer.getServiceIdByModelId(state, evaluatorModelId?.value));
    const toolOptions = chatGPTReducer.getAgentIQToolOptions();
    const selectedToolOptions = useSelector((state) => chatGPTReducer.getAgentIQTools(state));
    const [isLoadingCreateProfile, setIsLoadingCreateProfile] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const profiler = useSelector((state) => getAgentProfiler(state));
    const handleCreateAgentProfile = async () => {
        const body = {
            agent_type: "reasoning",
            executor_model_id: baseConnectionsModel ? `${baseConnectionsModel}` : "",
            reasoning_model_id: reasoningConnectionsModel ? `${reasoningConnectionsModel}` : "",
            intern_nlq_model_id: `${agentIQNLQModel}`,
            intern_base_model_id: `${modelId}`,
            evaluator_model_id: evaluatorConnectionsModel ? `${evaluatorConnectionsModel}` : "",
            reasoning_prompt: agentIQReasoningPrompt || "",
            tool_list: [...selectedToolOptions.map((t) => `${t.id}`), "2"],
            folder_id: `${selectedFolder}`
        };
        setIsLoadingCreateProfile(true);
        try {
            await dispatch(createAgentProfiler(body));
        }
        catch (err) {
            toast(`There was an error loading the profile\n${err}`, {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER
            });
        }
        setIsLoadingCreateProfile(false);
    };
    const handleCheckProfilerStatus = async (profiler) => {
        setIsLoadingStatus(true);
        const intervalId = setInterval(async () => {
            try {
                const status = await dispatch(getAgentProfilerStatus(profiler.profile_id));
                if (status?.profile_status && status.profile_status === "finished") {
                    clearInterval(intervalId);
                    setIsLoadingStatus(false);
                }
            }
            catch (err) {
                toast(`There was an error loading the profile\n${err}`, {
                    type: toast.TYPE.ERROR,
                    position: toast.POSITION.TOP_CENTER
                });
            }
        }, 2 * 60 * 1000);
    };
    const handleGetProfilerResults = async (profiler) => {
        setIsLoadingDetails(true);
        try {
            await dispatch(getAgentProfilerResults(profiler.profile_id));
        }
        catch (err) {
            toast(`There was an error loading the profile\n${err}`, {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER
            });
        }
        setIsLoadingDetails(false);
    };
    useEffect(() => {
        if (!profiler?.profile_id)
            return;
        if (profiler.profile_status === "pending") {
            handleCheckProfilerStatus(profiler);
        }
        if (profiler.profile_status === "finished") {
            handleGetProfilerResults(profiler);
        }
    }, [profiler?.profile_status]);
    const enableCreateSubmit = Boolean(modelId) &&
        Boolean(foundAgentIqBaseModel) &&
        Boolean(foundAgentIqNlqModel) &&
        Boolean(foundAgentIqReasoningModel) &&
        Boolean(agentIQReasoningPrompt?.length) &&
        Boolean(selectedToolOptions.length) &&
        Boolean(evaluatorModelId?.value);
    if (!profiler?.profile_id) {
        return (React.createElement("div", { style: {
                margin: "10px",
                backgroundColor: "#FFF",
                border: "1px solid #B5B4D0",
                borderRadius: "6px",
                padding: "10px"
            } },
            React.createElement("div", { style: { display: "flex", justifyContent: "center", padding: "10px" } },
                React.createElement("div", { className: styles.testReportHeaderTitle, style: { marginBottom: "5px" } }, "Create NVIDIA NeMoAgent Performance Profile")),
            React.createElement("div", { className: styles.testReportBody },
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "AI Model"),
                    React.createElement(ModelSelector, { modelId: modelIdWithPrefix, updateModelId: (val) => dispatch(chatGPTReducer.updateModelId(val)), hideIcons: true, onChange: selectOnChangeWrapper((val) => {
                            dispatch(chatGPTReducer.updateModelId(val));
                        }), isCreatable: true, selectorStyle: {
                            minWidth: "159px"
                        }, style: {
                            flexShrink: "1",
                            whiteSpace: "normal",
                            marginRight: "8px"
                        } })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Base Model"),
                    React.createElement(Select, { options: modelOptions, value: foundAgentIqBaseModel, onChange: (e) => dispatch(chatGPTReducer.updateAgentIQBaseModel(e?.value)) })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "NLQ Model"),
                    React.createElement(Select, { options: modelOptions, value: foundAgentIqNlqModel, onChange: (e) => dispatch(chatGPTReducer.updateAgentIQNLQModel(e?.value)) })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Reasoning Model"),
                    React.createElement(Select, { options: modelOptions, value: foundAgentIqReasoningModel, onChange: (e) => dispatch(chatGPTReducer.updateAgentIQReasoningModel(e?.value)) })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Evaluator Model"),
                    React.createElement(Select, { options: modelOptions, value: evaluatorModelId, onChange: (e) => setEvaluatorModelId(e) })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Agent Reasoning Instructions"),
                    React.createElement(TextInput, { className: styles.settingPrompt, value: agentIQReasoningPrompt ?? "", onChange: (val) => dispatch(chatGPTReducer.updateAgentIQReasoningPrompt(val, modelIdWithPrefix)) })),
                React.createElement("div", { style: { marginBottom: "15px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Tools"),
                    React.createElement(Select, { options: toolOptions, onChange: (selectedOption) => {
                            dispatch(chatGPTReducer.updateAgentIQTools(selectedOption));
                        }, value: selectedToolOptions, isMulti: true, placeholder: "Select All Tools" })),
                React.createElement(Button, { onClick: () => handleCreateAgentProfile(), isLoading: isLoadingCreateProfile, disabled: !enableCreateSubmit }, "Create new profile"))));
    }
    if (isLoadingStatus || isLoadingDetails) {
        return (React.createElement("div", { style: {
                margin: "10px",
                backgroundColor: "#FFF",
                border: "1px solid #B5B4D0",
                borderRadius: "6px",
                padding: "10px"
            } },
            React.createElement("div", { style: { display: "flex", justifyContent: "center", padding: "10px" } },
                React.createElement("div", { style: { padding: "2vw" } },
                    React.createElement(LoadingIndicator, { color: "#868595" }),
                    React.createElement("div", { style: { fontWeight: "300" } }, "Analyzing your Agent Profile... It may take more then 5 minutes.")))));
    }
    if (!profiler.results) {
        return (React.createElement("div", { style: {
                overflowY: "auto",
                margin: "10px",
                backgroundColor: "#FFF",
                border: "1px solid #B5B4D0",
                borderRadius: "6px",
                padding: "10px"
            } },
            React.createElement("div", { className: styles.testReportHeaderTitle, style: { marginBottom: "5px" } }, "Unexpected error")));
    }
    // Getting number with precision .x and correct logic of rounding
    const RAGRelevanceScore = Math.round(profiler.results["rag_relevance_output"].avg_score * 100 * 10) / 10;
    const RAGAccuracyScore = Math.round(profiler.results["rag_accuracy_output"].avg_score * 100 * 10) / 10;
    const RAGGroundednessScore = Math.round(profiler.results["rag_groundedness_output"].avg_score * 100 * 10) / 10;
    const TrajectoryScore = Math.round(profiler.results["trajectory_accuracy_output"].avg_score * 100 * 10) / 10;
    return (React.createElement("div", { style: {
            overflowY: "auto",
            margin: "10px",
            backgroundColor: "#FFF",
            border: "1px solid #B5B4D0",
            borderRadius: "6px",
            padding: "10px"
        } },
        React.createElement("div", { style: {
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: "10px",
                marginBottom: "15px"
            } },
            React.createElement("div", null,
                React.createElement("div", { className: styles.testReportHeaderTitle, style: { marginBottom: "5px" } }, "NVIDIA NeMoAgent Performance Monitoring"),
                React.createElement("div", { className: styles.testReportHeaderSubtitle, style: { fontSize: "13px" } }, "An overview of the High Agent autonomy performance")),
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "5px" } }, profiler.start_time && (React.createElement(Tooltip, { overlay: format(new Date(profiler.start_time), "MM/dd/yyyy, hh:mm aaa") },
                React.createElement("div", { className: styles.testReportHeaderSubtitle, style: { fontSize: "14px" } }, formatDistance(new Date(profiler.start_time), new Date(), {
                    addSuffix: true
                })))))),
        React.createElement("div", { className: styles.testReportBody },
            React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "10px",
                    marginBottom: "10px"
                } },
                React.createElement("div", { style: {
                        padding: "10px",
                        border: "1px solid #B5B4D0",
                        borderRadius: "6px",
                        flex: "1 1"
                    } },
                    React.createElement("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            marginBottom: "5px"
                        } },
                        React.createElement("div", { style: {
                                fontSize: "13px",
                                fontWeight: "700",
                                marginBottom: "5px"
                            } }, "RAG Relevance")),
                    React.createElement("div", { style: { fontSize: "24px", marginBottom: "5px" } },
                        RAGRelevanceScore,
                        "%"),
                    React.createElement(ProgressBar, { bsStyle: getProgressBarColor(RAGRelevanceScore), now: RAGRelevanceScore, style: { height: "10px" } })),
                React.createElement("div", { style: {
                        padding: "10px",
                        border: "1px solid #B5B4D0",
                        borderRadius: "6px",
                        flex: "1 1"
                    } },
                    React.createElement("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            marginBottom: "5px"
                        } },
                        React.createElement("div", { style: {
                                fontSize: "13px",
                                fontWeight: "700",
                                marginBottom: "5px"
                            } }, "RAG Accuracy")),
                    React.createElement("div", { style: { fontSize: "24px", marginBottom: "5px" } },
                        RAGAccuracyScore,
                        "%"),
                    React.createElement(ProgressBar, { bsStyle: getProgressBarColor(RAGAccuracyScore), now: RAGAccuracyScore, style: { height: "10px" } })),
                React.createElement("div", { style: {
                        padding: "10px",
                        border: "1px solid #B5B4D0",
                        borderRadius: "6px",
                        flex: "1 1"
                    } },
                    React.createElement("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            marginBottom: "5px"
                        } },
                        React.createElement("div", { style: {
                                fontSize: "13px",
                                fontWeight: "700",
                                marginBottom: "5px"
                            } }, "RAG Groundedness")),
                    React.createElement("div", { style: { fontSize: "24px", marginBottom: "5px" } },
                        RAGGroundednessScore,
                        "%"),
                    React.createElement(ProgressBar, { bsStyle: getProgressBarColor(RAGGroundednessScore), now: RAGGroundednessScore, style: { height: "10px" } })),
                React.createElement("div", { style: {
                        padding: "10px",
                        border: "1px solid #B5B4D0",
                        borderRadius: "6px",
                        flex: "1 1"
                    } },
                    React.createElement("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            marginBottom: "5px"
                        } },
                        React.createElement("div", { style: {
                                fontSize: "13px",
                                fontWeight: "700",
                                marginBottom: "5px"
                            } }, "Trajectory Accuracy")),
                    React.createElement("div", { style: { fontSize: "24px", marginBottom: "5px" } },
                        TrajectoryScore,
                        "%"),
                    React.createElement(ProgressBar, { bsStyle: getProgressBarColor(TrajectoryScore), now: TrajectoryScore, style: { height: "10px" } }))),
            React.createElement("div", { style: {
                    padding: "10px",
                    border: "1px solid #B5B4D0",
                    borderRadius: "6px"
                } },
                React.createElement("div", { style: { fontSize: "13px", fontWeight: "700", marginBottom: "5px" } }, "Processing timeline for all agent steps"),
                React.createElement("div", { style: { fontSize: "13px", marginBottom: "5px", color: "#697586" } }, "Timeline shows function calls, LLM processing, and tool executions across multiple agent runs"),
                profiler.results.charts && React.createElement(AgentProfilerCharts, { charts: profiler.results.charts })))));
};
export default AgentProfiler;
